import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Wallet, Clock, History, Download, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatEur = (n) => `\u20AC${Number(n || 0).toFixed(2)}`;
const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};
const monthLabel = (m) => new Date(m + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const yearMonths = (startYear) => {
    const out = [];
    for (let m = 9; m <= 12; m++) out.push(`${startYear}-${String(m).padStart(2, '0')}`);
    for (let m = 1; m <= 8; m++) out.push(`${startYear + 1}-${String(m).padStart(2, '0')}`);
    return out;
};
const CURRENT_MONTHS = yearMonths(2026); // 2026-2027
const PREV_MONTHS = yearMonths(2025);    // 2025-2026
const todayKey = () => new Date().toISOString().slice(0, 7);

const TeacherSalary = () => {
    const tk = todayKey();
    const [year, setYear] = useState(tk < '2026-09' ? 'previous' : 'current');
    const [month, setMonth] = useState(() => {
        if (tk < '2026-09') return tk >= '2025-09' ? tk : '2025-09';
        return tk <= '2027-08' ? tk : '2027-08';
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [pdfBusy, setPdfBusy] = useState(false);

    const months = year === 'current' ? CURRENT_MONTHS : PREV_MONTHS;

    const changeYear = (y) => {
        setYear(y);
        const list = y === 'current' ? CURRENT_MONTHS : PREV_MONTHS;
        setMonth(list.includes(tk) ? tk : list[0]);
    };

    useEffect(() => {
        let abort = false;
        setLoading(true);
        const url = year === 'current'
            ? `${API_URL}/api/teacher-salary/me?month=${month}`
            : `${API_URL}/api/teacher-salary/me/previous-year?month=${month}`;
        axios.get(url, { withCredentials: true })
            .then((res) => { if (!abort) setData(res.data); })
            .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load salary'))
            .finally(() => { if (!abort) setLoading(false); });
        return () => { abort = true; };
    }, [year, month]);

    useEffect(() => {
        if (year !== 'previous') { setOverview(null); return; }
        axios.get(`${API_URL}/api/teacher-salary/me/previous-year`, { withCredentials: true })
            .then(res => setOverview(res.data))
            .catch(() => {});
    }, [year]);

    const totalDuration = useMemo(() => data ? data.sessions.reduce((s, r) => s + (r.duration || 0), 0) : 0, [data]);

    const downloadPdf = async () => {
        setPdfBusy(true);
        try {
            const res = await axios.get(`${API_URL}/api/teacher-salary/me/previous-year/pdf`, { withCredentials: true, responseType: 'blob' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(res.data);
            link.download = `salary_2025-2026.pdf`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch { toast.error('Failed to download PDF'); }
        finally { setPdfBusy(false); }
    };

    if (loading && !data) {
        return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;
    }
    if (!data) return <DashboardLayout><div className="p-8 text-center text-[#4B5563]">No salary data available.</div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teacher-salary-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">My Salary</h1>
                        <p className="text-[#4B5563]">{data.teacher_name} · School year {data.school_year}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={year} onValueChange={changeYear}>
                            <SelectTrigger className="w-52" data-testid="year-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="current">2026-2027 (Current)</SelectItem>
                                <SelectItem value="previous">2025-2026 (Previous)</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger className="w-52" data-testid="month-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {months.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {year === 'previous' && (
                            <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="prev-year-pdf-btn">
                                {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}PDF
                            </Button>
                        )}
                    </div>
                </div>

                {/* Table 1 — Month summary */}
                <Card className="card-surface" data-testid="month-summary-card">
                    <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><Wallet className="h-5 w-5" /> {data.month_label} — Summary{data.source === 'uploaded' && <Badge variant="outline" className="ml-2 text-xs">Uploaded records</Badge>}</CardTitle></CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <Stat label="Hours worked" value={`${data.hours_worked.toFixed(2)} h`} />
                            <Stat label="Hourly rate" value={formatEur(data.hourly_rate)} />
                            <Stat label="Total" value={formatEur(data.total_deserved)} accent />
                            <Stat label="Paid this month" value={formatEur(data.paid)} />
                            <Stat label="Balance" value={formatEur(data.month_balance)} positive={data.month_balance >= 0} />
                        </div>
                    </CardContent>
                </Card>

                {/* Table 2 — Sessions */}
                <Card className="card-surface">
                    <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><Clock className="h-5 w-5" /> Hours Details</CardTitle></CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Details</TableHead>
                                        <TableHead>Start</TableHead>
                                        <TableHead>End</TableHead>
                                        <TableHead className="text-right">Duration</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.sessions.length === 0 ? (
                                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#4B5563]">No sessions in this month yet.</TableCell></TableRow>
                                    ) : data.sessions.map(s => {
                                        const red = s.is_absent;
                                        return (
                                            <TableRow key={s.id} className={red ? 'bg-[#FEF2F2]' : ''} data-testid={`session-${s.id}`}>
                                                <TableCell className={red ? 'text-[#EF4444]' : ''}>{formatDate(s.date)}</TableCell>
                                                <TableCell className={red ? 'text-[#EF4444]' : ''}>
                                                    {s.details}
                                                    {s.note && <span className="ml-2 text-xs font-semibold">{s.note}</span>}
                                                </TableCell>
                                                <TableCell className={`tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{s.start_time}</TableCell>
                                                <TableCell className={`tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{s.end_time}</TableCell>
                                                <TableCell className={`text-right tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{(s.duration || 0).toFixed(2)} h</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
                            <span className="text-sm text-[#4B5563]">Total duration this month</span>
                            <span className="font-semibold tabular-nums" data-testid="total-duration">{totalDuration.toFixed(2)} h</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Table 3 — Balance summary */}
                <Card className="card-surface" data-testid="balance-summary-card">
                    <CardHeader><CardTitle className="font-['Outfit']">Balance Summary</CardTitle></CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Stat label="Previous balance (carry-over)" value={formatEur(data.previous_balance)} positive={data.previous_balance >= 0} />
                            <Stat label="This month balance" value={formatEur(data.month_balance)} positive={data.month_balance >= 0} />
                            <Stat label="Final balance" value={formatEur(data.final_balance)} positive={data.final_balance >= 0} accent />
                        </div>
                    </CardContent>
                </Card>

                {/* Previous year overview */}
                {year === 'previous' && overview && overview.has_assignments && (
                    <Card className="card-surface" data-testid="prev-year-salary-card">
                        <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><History className="h-5 w-5" /> Year Overview ({overview.year})</CardTitle></CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Month</TableHead>
                                        <TableHead className="text-right">Hours</TableHead>
                                        <TableHead className="text-right">Earned</TableHead>
                                        <TableHead className="text-right">Paid</TableHead>
                                        <TableHead className="text-right">Balance</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {overview.months.map(m => (
                                            <TableRow key={m.month} className={m.month === month ? 'bg-[#064E3B]/5' : ''}>
                                                <TableCell>
                                                    {m.month_label}
                                                    {m.pro_bono_sessions > 0 && <Badge variant="outline" className="text-xs ml-2">Pro Bono ×{m.pro_bono_sessions}</Badge>}
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">{m.hours.toFixed(2)} h</TableCell>
                                                <TableCell className="text-right tabular-nums">{formatEur(m.earned)}</TableCell>
                                                <TableCell className="text-right tabular-nums">{formatEur(m.paid)}</TableCell>
                                                <TableCell className={`text-right tabular-nums ${m.balance > 0 ? 'text-[#EF4444]' : ''}`}>{formatEur(m.balance)}</TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="bg-[#064E3B]/5 font-semibold">
                                            <TableCell>Year Total</TableCell>
                                            <TableCell className="text-right tabular-nums">{overview.totals.hours.toFixed(2)} h</TableCell>
                                            <TableCell className="text-right tabular-nums">{formatEur(overview.totals.earned)}</TableCell>
                                            <TableCell className="text-right tabular-nums">{formatEur(overview.totals.paid)}</TableCell>
                                            <TableCell className={`text-right tabular-nums ${overview.totals.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{formatEur(overview.totals.balance)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </DashboardLayout>
    );
};

const Stat = ({ label, value, accent, positive }) => (
    <div className={`rounded-lg p-3 border ${accent ? 'bg-[#064E3B] border-[#064E3B] text-white' : 'bg-white border-[#E5E7EB]'}`}>
        <p className={`text-xs ${accent ? 'text-white/80' : 'text-[#4B5563]'}`}>{label}</p>
        <p className={`text-lg font-bold tabular-nums ${accent ? 'text-white' : (positive === false ? 'text-[#EF4444]' : 'text-[#1F2937]')}`}>{value}</p>
    </div>
);

export default TeacherSalary;
